import React, { useMemo, useState } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  View,
  ViewStyle,
} from 'react-native';
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { useTheme, GTFonts, useLayoutSize } from '../../theme';
import {
  fs,
  s,
  v,
  borderWidth,
  formatWithCommas,
  sanitizeNumericInput,
  roundToNumericPrecision,
} from '../../utils/libs';
import QuantityControlButton from './QuantityControlButton';
import Typography from './Typography';
import { isTablet } from 'react-native-device-info';
import { shouldShowTabletSidebarLayoutSetting } from '../../utils/layoutUtils';

interface QuantityControlsProps {
  /**
   * Product pricing model (domain enum):
   *   'item' = sold by piece — renders +/input/- buttons.
   *   'unit' = sold by mass/volume — renders mass input with unit suffix.
   * `gestureMode` is orthogonal and overrides the controls UI for either
   * priceType when the user is in gesture-quantity mode.
   */
  priceType: 'item' | 'unit';
  /** Current quantity (used by 'item' and gesture-mode label) */
  quantity: number;
  /** Current mass value (for unit type) */
  mass?: number;
  /** Mass input text value - allows controlled input with trailing decimals */
  massText?: string;
  /** Unit of mass suffix (e.g., 'kg', 'g') */
  unitOfMass?: string;
  /** Called when + button pressed */
  onAdd: () => void;
  /** Called when - button pressed */
  onSubtract: () => void;
  /** Called when quantity input changes (item type) */
  onQuantityChange: (value: string) => void;
  /** Called when mass input changes (unit type) */
  onMassChange?: (value: string) => void;
  /** Called when mass input gains focus */
  onMassFocus?: () => void;
  /** Called when mass input loses focus */
  onMassBlur?: () => void;
  /** Called when item-qty input loses focus */
  onQuantityBlur?: () => void;
  /**
   * Fires whenever the internal TextInput (item OR unit) gains focus.
   * Used by product cards to ask their parent list to scroll them above
   * the keyboard — the platform's automaticallyAdjustKeyboardInsets does
   * not reliably scroll a FlashList row into view.
   */
  onInputFocus?: () => void;
  /** Maximum allowed quantity (disables + when reached) */
  maxQuantity?: number;
  /** Minimum allowed quantity (disables - when reached) */
  minQuantity?: number;
  /** Disable all controls */
  disabled?: boolean;
  /** Force web layout (Pressable with hover). Auto-detected if not provided.
   *  For `priceType="unit"`, mass input still uses non-web size tokens internally;
   *  pass `true` on web to match item callers — styling matches old `false`. */
  useWebLayout?: boolean;
  /**
   * Use compact sizing for tablet product cards. Auto-detected from
   * `isTablet()` when not provided so consumers can't accidentally render
   * the desktop size on a tablet (which was the source of card-height
   * drift between Product/Cart/Partial screens).
   */
  compact?: boolean;
  /** Show error styling on input */
  hasError?: boolean;
  /** Hide the - button (for product cards where - only shows when qty > 0) */
  hideSubtractWhenZero?: boolean;
  /**
   * Mirror layout for lines that can only be reduced (order edits): renders
   * `- input +`. The - and input always show; the + appears once the
   * quantity drops below `maxQuantity`.
   */
  reduceOnly?: boolean;
  /** Show disabled styling on + button but keep it pressable (for out-of-stock with snackbar) */
  showPlusDisabledStyle?: boolean;
  /**
   * UI mode override — when true, the controls UI is replaced with a
   * height-locked "X N items" label used by gesture-quantity flow.
   * Independent of `priceType` (the domain pricing model); typically only
   * meaningful for `priceType="item"` since the gesture flow disables itself
   * for unit-priced products.
   */
  gestureMode?: boolean;
  /**
   * Style applied to the outermost element of the component, replacing the
   * ad-hoc `btnQuantity` / `bottomleft` / `controlsWrapperHorizontal`
   * wrappers each consumer used to invent.
   */
  containerStyle?: StyleProp<ViewStyle>;
  /**
   * When true, the outer element gets a `minHeight` matching the tallest
   * variant (unit input). Use this in multi-column product grids so cards
   * showing only `+` (item, qty=0) line up with cards showing the full
   * `+ input -` row or the unit input. Required to avoid the
   * tablet-portrait height divergence in row-layout product cards.
   */
  fixedHeight?: boolean;
  /**
   * When true, use gorhom BottomSheetTextInput so the sheet can lift
   * above the keyboard. Only pass from inside a BottomSheet.
   */
  useBottomSheetInput?: boolean;
}

const QuantityControls: React.FC<QuantityControlsProps> = ({
  priceType,
  quantity,
  mass = 0,
  massText,
  unitOfMass = '',
  onAdd,
  onSubtract,
  onQuantityChange,
  onMassChange,
  onMassFocus,
  onMassBlur,
  onQuantityBlur,
  maxQuantity,
  minQuantity = 0,
  disabled = false,
  useWebLayout: useWebLayoutProp,
  compact: compactProp,
  hasError = false,
  hideSubtractWhenZero = false,
  reduceOnly = false,
  showPlusDisabledStyle = false,
  gestureMode = false,
  containerStyle,
  fixedHeight = false,
  onInputFocus,
  useBottomSheetInput = false,
}) => {
  const { theme } = useTheme();
  const useWebLayout = useWebLayoutProp ?? Platform.OS === 'web';
  const { width, height } = useLayoutSize();
  const showTabletSidebarLayoutSetting = shouldShowTabletSidebarLayoutSetting(
    width,
    height,
  );
  const compact = compactProp ?? isTablet();
  const styles = useMemo(
    () =>
      createStyles(
        theme,
        useWebLayout,
        compact,
        showTabletSidebarLayoutSetting,
      ),
    [theme, useWebLayout, compact, showTabletSidebarLayoutSetting],
  );
  const Input = useBottomSheetInput ? BottomSheetTextInput : TextInput;

  const [itemFocused, setItemFocused] = useState(false);

  const isMaxed = maxQuantity !== undefined && quantity >= maxQuantity;
  const isMinned = quantity <= minQuantity;
  const showSubtract = hideSubtractWhenZero ? (quantity > 0 || itemFocused) : true;
  const showQuantityInput = hideSubtractWhenZero ? (quantity > 0 || itemFocused) : true;

  // Format number with commas for display, preserving decimals for unit type
  const formatDisplayValue = (value: string | number, isDecimal: boolean) => {
    if (value === '' || value === undefined || value === null) return '';
    const strVal = String(value);
    if (strVal === '') return '';
    if (!isDecimal && strVal === '0') return '';

    // For decimal inputs, preserve trailing decimal point and zeros
    if (isDecimal) {
      const hasTrailingDecimal = strVal.endsWith('.');
      const decimalMatch = strVal.match(/\.(\d*)$/);
      const decimalPart = decimalMatch ? decimalMatch[1] : '';

      const numericValue = parseFloat(strVal.replace(/,/g, ''));
      if (isNaN(numericValue)) return strVal;

      // Format the integer part with commas
      const formatted = formatWithCommas(Math.floor(numericValue), 0);

      if (hasTrailingDecimal) {
        return `${formatted}.${decimalPart}`;
      } else if (decimalPart) {
        return `${formatted}.${decimalPart}`;
      }
      return formatted;
    }

    // For integers, simple comma formatting
    const numericValue = parseInt(strVal.replace(/,/g, ''), 10);
    if (isNaN(numericValue)) return strVal;
    return formatWithCommas(numericValue, 0);
  };

  // Unit inputs keep draft decimal text, but cap precision app-wide.
  const handleMassInputChange = (input: string) => {
    const cleanValue = sanitizeNumericInput(input, { isUnit: true });
    onMassChange?.(cleanValue);
  };

  const handleQuantityInputChange = (input: string) => {
    const cleanValue = input.replace(/,/g, '');
    onQuantityChange(cleanValue);
  };

  if (gestureMode) {
    const hasQty = quantity > 0;
    return (
      <View
        style={[
          styles.gestureContainer,
          fixedHeight && styles.outerFixed,
          containerStyle,
        ]}>
        <Typography
          style={[styles.gestureText, !hasQty && styles.gestureTextEmpty]}>
          X {quantity}
          {'\u00A0'}
          {quantity === 1 ? 'item' : 'items'}
        </Typography>
      </View>
    );
  }

  if (priceType === 'unit') {
    const rawValue =
      massText !== undefined
        ? massText
        : mass > 0
          ? String(roundToNumericPrecision(mass))
          : '';
    const displayValue = formatDisplayValue(rawValue, true);

    return (
      <View
        style={[
          styles.unitInputContainer,
          hasError && styles.errorBorder,
          fixedHeight && styles.outerFixedUnit,
          containerStyle,
        ]}>
        <Input
          style={styles.unitInput}
          keyboardType="decimal-pad"
          inputMode="decimal"
          maxLength={10}
          placeholder="0"
          placeholderTextColor={theme.colors.textTertiary}
          value={displayValue}
          onFocus={() => {
            onMassFocus?.();
            onInputFocus?.();
          }}
          onBlur={onMassBlur}
          onChangeText={handleMassInputChange}
          editable={!disabled}
        />
        <View style={styles.unitSuffixContainer}>
          <Typography style={styles.unitSuffix}>{unitOfMass}</Typography>
        </View>
      </View>
    );
  }

  if (reduceOnly) {
    const showPlus = !isMaxed;
    return (
      <View
        style={[styles.row, fixedHeight && styles.outerFixed, containerStyle]}>
        <QuantityControlButton
          variant="minus"
          onPress={onSubtract}
          disabled={disabled || isMinned}
          useWebLayout={useWebLayout}
          compact={compact}
        />
        <Input
          style={[styles.quantityInput, hasError && styles.errorBorder]}
          keyboardType="numeric"
          inputMode="numeric"
          maxLength={7}
          value={formatDisplayValue(quantity, false)}
          onFocus={() => {
            setItemFocused(true);
            onInputFocus?.();
          }}
          onBlur={() => {
            onQuantityBlur?.();
            setItemFocused(false);
          }}
          onChangeText={handleQuantityInputChange}
          editable={!disabled}
        />
        {showPlus && (
          <QuantityControlButton
            variant="plus"
            onPress={onAdd}
            disabled={disabled}
            useWebLayout={useWebLayout}
            compact={compact}
          />
        )}
      </View>
    );
  }

  return (
    <View
      style={[styles.row, fixedHeight && styles.outerFixed, containerStyle]}>
      <QuantityControlButton
        variant="plus"
        onPress={onAdd}
        disabled={disabled || isMaxed}
        showDisabledStyle={showPlusDisabledStyle}
        useWebLayout={useWebLayout}
        compact={compact}
      />
      {showQuantityInput && (quantity > 0 || itemFocused) && (
        <Input
          style={[styles.quantityInput, hasError && styles.errorBorder]}
          keyboardType="numeric"
          inputMode="numeric"
          maxLength={7}
          value={formatDisplayValue(quantity, false)}
          onFocus={() => {
            setItemFocused(true);
            onInputFocus?.();
          }}
          onBlur={() => {
            onQuantityBlur?.();
            setItemFocused(false);
          }}
          onChangeText={handleQuantityInputChange}
          editable={!disabled}
        />
      )}
      {showSubtract && (quantity > 0 || itemFocused) && (
        <QuantityControlButton
          variant="minus"
          onPress={onSubtract}
          disabled={disabled || isMinned}
          useWebLayout={useWebLayout}
          compact={compact}
        />
      )}
    </View>
  );
};

export default QuantityControls;

const createStyles = (
  theme: any,
  useWebLayout: boolean,
  compact: boolean,
  showTabletSidebarLayoutSetting: boolean,
) => {
  // Item row (+ / qty / −) and gesture label follow `useWebLayout` so callers
  // can pass useWebLayout={true} on web consistently with Product.web.
  //
  // Unit mass input always uses the same style tokens as useWebLayout === false
  // (compact/native metrics): web layout for that field was problematic, but
  // callers still pass useWebLayout={true} for API parity with item rows.
  // const unitWebLayout = false;

  const ROW_HEIGHT_ITEM = v(
    showTabletSidebarLayoutSetting ? 37 : useWebLayout ? 37 : compact ? 40 : 35,
  );
  const ROW_HEIGHT_UNIT = v(
    showTabletSidebarLayoutSetting ? 37 : compact ? 40 : 35,
  );
  const INPUT_WIDTH = s(
    showTabletSidebarLayoutSetting ? 60 : useWebLayout ? 80 : compact ? 70 : 60,
  );
  const UNIT_CONTAINER_WIDTH = s(useWebLayout ? 130 : compact ? 130 : 100);
  const UNIT_INPUT_MIN_WIDTH = s(useWebLayout ? 70 : 60);

  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(useWebLayout ? 8 : compact ? 4 : 6),
    },
    outerFixed: {
      minHeight: ROW_HEIGHT_ITEM,
    },
    outerFixedUnit: {
      minHeight: ROW_HEIGHT_UNIT,
    },
    quantityInput: {
      width: INPUT_WIDTH,
      height: ROW_HEIGHT_ITEM,
      borderWidth: borderWidth(useWebLayout ? 0.5 : 1),
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      textAlign: 'left',
      fontFamily: GTFonts.regular,
      fontSize: fs(useWebLayout ? 18 : 16),
      color: theme.colors.text,
      paddingVertical: 0,
      paddingHorizontal: s(4),
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        boxShadow: 'none',
      }),
    } as any,
    unitInputContainer: {
      flexDirection: 'row',
      alignItems: 'stretch',
      // minWidth: UNIT_CONTAINER_WIDTH,
      width: UNIT_CONTAINER_WIDTH,
      borderWidth: borderWidth(useWebLayout ? 0.5 : 1),
      borderColor: theme.colors.borderGray,
      borderRadius: theme.borderRadius.md,
      height: ROW_HEIGHT_UNIT,
      backgroundColor: theme.colors.surface,
      overflow: 'hidden',
    },
    unitInput: {
      flex: 1,
      height: '100%',
      minWidth: UNIT_INPUT_MIN_WIDTH,
      paddingHorizontal: s(8),
      paddingVertical: 0,
      fontFamily: GTFonts.semibold,
      fontSize: fs(useWebLayout ? 18 : 16),
      color: theme.colors.text,
      ...(Platform.OS === 'web' && {
        outlineStyle: 'none',
        outlineWidth: 0,
        outlineColor: 'transparent',
        boxShadow: 'none',
      }),
    } as any,
    unitSuffixContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: s(8),
    },
    unitSuffix: {
      fontFamily: GTFonts.regular,
      fontSize: fs(useWebLayout ? 16 : 14),
      lineHeight: fs(20),
      color: theme.colors.textSecondary,
      textAlign: 'center',
    },
    // Gesture-mode "X N items" label. Same height as the input variants so
    // a row of cards (some showing the label, some the input) lines up.
    gestureContainer: {
      height: ROW_HEIGHT_ITEM,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: s(8),
    },
    gestureText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(useWebLayout ? 14 : 18),
      color: theme.colors.success,
    },
    // Hidden but height-preserving — keeps card height stable when qty=0.
    gestureTextEmpty: {
      opacity: 0,
    },
    errorBorder: {
      borderColor: theme.colors.error,
    },
  });
};
